import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  MapPin,
  Phone,
  MessageSquare,
  ChevronRight,
  ShieldCheck,
  Sun,
  Smile,
  CheckCircle2,
  HelpCircle,
  UtensilsCrossed,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { RESTAURANT_INFO, CLIENT_IMAGES } from '../data/restaurantData';
import { ClientImage } from '../components/ClientImage';

interface RideItem {
  id: string;
  name: string;
  category: string;
  tagline: string;
  description: string;
  image: string;
  fallbackImage: string;
  slotKey: string;
  features: string[];
  bestFor: string;
}

const RIDES_LIST: RideItem[] = [
  {
    id: 'carousel',
    name: "Classic Children's Carousel",
    category: 'Traditional Fairground',
    tagline: 'Bright lights, cheerful tunes & timeless horses',
    description:
      "A seaside favourite right on Ramsgate Main Sands! Beautifully restored merry-go-round horses and whimsical carriage seats designed specially for younger children and toddlers accompanied by parents.",
    image: '/childern_4.webp',
    fallbackImage: '/childern_2.webp',
    slotKey: 'rides:carousel',
    features: ['Gentle & safe for little ones', 'Parents can assist toddlers', 'Cheerful seaside fairground music', 'Traditional carousel horses & cars'],
    bestFor: 'Toddlers & Young Children (Ages 2–8)',
  },
  {
    id: 'kiddies-corner',
    name: 'Kiddies Safari & Mini Car Rides',
    category: 'Fairground Rides',
    tagline: 'Little steering wheels, fun vehicles & big smiles',
    description:
      'Children can jump in the driver’s seat of mini sports cars, fire trucks, and jungle safari jeeps. Operating in a safe enclosed track on the beachfront directly adjacent to Frankie’s seating promenade.',
    image: '/childern_2.webp',
    fallbackImage: '/childern_4.webp',
    slotKey: 'rides:kiddies-corner',
    features: ['Interactive steering wheels & horns', 'Enclosed safety rails', 'Smooth gentle cruising speed', 'Great photo opportunity for parents'],
    bestFor: 'Ages 2 to 9',
  },
  {
    id: 'super-slide',
    name: 'Giant Inflatable Super Slide',
    category: 'Inflatable Adventures',
    tagline: 'Climb high, slide fast & land on soft cushions',
    description:
      'Towering inflatable beach slide offering hours of energetic bouncing fun. Kids can scramble up the foam steps and race down the slippery slope onto soft padded impact zones right on the sands.',
    image: '/childern_1.webp',
    fallbackImage: '/childern_3.webp',
    slotKey: 'rides:super-slide',
    features: ['High-walled safety inflatable sides', 'Soft cushioned landing pads', 'Continuous supervised airflow', 'Endless energy-burning beach fun'],
    bestFor: 'Ages 4 to 12',
  },
  {
    id: 'bungee-trampolines',
    name: 'Bungee Trampoline Jumpers',
    category: 'High-Flying Thrills',
    tagline: 'Soar into the seaside sky with secure harnesses',
    description:
      'Secure elastic bungee cords attached to comfortable safety harnesses allow kids to somersault, jump higher than ever, and experience gravity-defying beach flips with total peace of mind.',
    image: '/childern_3.webp',
    fallbackImage: '/childern_1.webp',
    slotKey: 'rides:bungee-trampolines',
    features: ['Full-body safety harnesses', 'Individual dedicated trampoline zones', 'Staff-assisted launching', 'Safe acrobatic flips and jumps'],
    bestFor: 'Ages 4 to 14',
  },
];

export const RidesPage: React.FC = () => {
  const handleWhatsAppInquiry = () => {
    const message = encodeURIComponent(
      "Hello Frankie's! 🎠 We'd like to ask about the children's beach rides and amusements on Ramsgate Sands."
    );
    window.open(`https://wa.me/${RESTAURANT_INFO.contact.whatsappNumber}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex-1 bg-[#EEEFE9] min-h-screen">
      {/* 1. Hero Header Banner */}
      <div className="bg-gradient-to-b from-[#0070E0] via-[#005bb5] to-[#004b96] text-white py-12 sm:py-16 relative overflow-hidden">
        {/* Decorative background glows */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-xs text-sky-200 uppercase font-heading font-bold mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-sky-300" />
            <span className="text-[#ECD87A]">Beach Rides &amp; Amusements</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-400 text-black text-xs font-heading font-black uppercase tracking-wider mb-4 shadow-md">
              <Sparkles className="w-3.5 h-3.5" />
              <span>RAMSGATE MAIN SANDS • FAMILY &amp; KIDS FUNFAIR</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black font-heading tracking-tight uppercase leading-tight">
              CHILDREN’S FAIR RIDES &amp; BEACH AMUSEMENTS
            </h1>

            <p className="mt-3 text-sm sm:text-base text-sky-100 leading-relaxed max-w-2xl font-medium">
              Create unforgettable summer seaside memories! From our traditional fairground carousel and mini safari cars to giant slides and bungee trampolines, the kids’ funfair sits directly beside Frankie’s food kiosk on Ramsgate Main Sands.
            </p>

            {/* Quick Action Buttons */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleWhatsAppInquiry}
                className="bg-[#25D366] hover:bg-[#20ba5a] text-white font-heading font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 transition-transform active:scale-95 cursor-pointer"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Ask via WhatsApp</span>
              </button>

              <Link
                to="/contact"
                className="bg-white/15 hover:bg-white/25 text-white font-heading font-bold text-xs uppercase tracking-wider px-5 py-3 rounded-xl border border-white/20 flex items-center gap-2 transition-all cursor-pointer"
              >
                <MapPin className="w-4 h-4 text-amber-300" />
                <span>Find Us On The Sands</span>
              </Link>

              <Link
                to="/menu?category=donuts"
                className="bg-[#FFD000] hover:bg-[#FFD933] text-black font-heading font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer"
              >
                <span>Fresh Warm Donuts</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Key Highlights Bar */}
      <div className="bg-white border-b border-[#dde0d5] py-4 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-bold text-[#000000]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Sun className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <div className="font-heading uppercase text-amber-800">Seasonal Daily Fun</div>
                <div className="text-[#526b74] font-normal text-[11px]">Open throughout sunny days &amp; school holidays</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#0070E0] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading uppercase text-[#0070E0]">Direct On The Sands</div>
                <div className="text-[#526b74] font-normal text-[11px]">Right beside Frankie's food kiosk &amp; sun loungers</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <div className="font-heading uppercase text-emerald-800">Child-Friendly &amp; Safe</div>
                <div className="text-[#526b74] font-normal text-[11px]">Regular safety inspections &amp; supervised rides</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Rides Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#0070E0] font-heading mb-1">
            <Smile className="w-4 h-4 text-[#D1A03F]" />
            <span>FAMILY FUN ON RAMSGATE BEACH</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black font-heading uppercase text-[#000000] tracking-tight">
            POPULAR BEACH RIDES &amp; ATTRACTIONS
          </h2>
          <p className="text-xs sm:text-sm text-[#526b74] mt-2">
            Grab a coffee, ice cream, or hot burger at Frankie’s and relax while your children enjoy the beach amusements.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {RIDES_LIST.map((ride) => (
            <div
              key={ride.id}
              className="bg-white rounded-3xl overflow-hidden border border-[#dde0d5] shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
            >
              {/* Photo Container */}
              <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                <ClientImage
                  slotKey={ride.slotKey}
                  src={ride.image}
                  fallbackSrc={ride.fallbackImage}
                  alt={ride.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                  <span className="bg-[#0070E0] text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-md tracking-wider">
                    {ride.category}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3 bg-black/75 backdrop-blur-xs text-white text-[10px] font-bold px-2.5 py-1 rounded-lg">
                  {ride.bestFor}
                </div>
              </div>

              {/* Ride Content */}
              <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <div className="text-xs font-extrabold uppercase text-[#D1A03F] tracking-wider font-heading">
                    {ride.tagline}
                  </div>
                  <h3 className="text-xl font-black font-heading uppercase text-[#000000] mt-0.5 group-hover:text-[#0070E0] transition-colors">
                    {ride.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#496068] mt-2 leading-relaxed">
                    {ride.description}
                  </p>
                </div>

                {/* Features Pill List */}
                <div className="space-y-1.5 pt-2 border-t border-[#edf0e6]">
                  <div className="text-[11px] font-extrabold uppercase tracking-wider text-[#000000] font-heading mb-1">
                    Ride Highlights:
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {ride.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-xs text-[#526b74]">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#0070E0] shrink-0" />
                        <span className="line-clamp-1">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4. Food & Treats Section Right Beside The Rides */}
      <div className="bg-[#edf0e6] py-12 sm:py-16 border-y border-[#dde0d5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-6 sm:p-10 border border-[#dde0d5] shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-7 space-y-4 text-left">
                <div className="inline-flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-[#0070E0] font-heading bg-sky-50 border border-sky-200/80 px-3.5 py-1.5 rounded-full">
                  <UtensilsCrossed className="w-4 h-4 text-[#D1A03F]" />
                  <span>REFRESHMENTS &amp; SEASIDE BITES</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-extrabold uppercase text-[#000000] font-heading leading-tight">
                  ENJOY FOOD &amp; DRINKS WHILE THE CHILDREN PLAY
                </h3>

                <p className="text-sm text-[#496068] leading-relaxed">
                  You don’t have to pack up and walk away to find refreshments. Frankie’s @ The Beach sits directly beside the fairground rides! Parents can sit comfortably on our deck chairs or promenade seating with a freshly cooked 6oz steak burger, a cold draft pint, a rich barista latte, or hot sugared donuts for the kids.
                </p>

                <div className="pt-2 flex flex-wrap items-center gap-3">
                  <Link
                    to="/menu"
                    className="bg-[#0070E0] hover:bg-[#005bb5] text-white font-heading font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <span>Full Food Menu</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <Link
                    to="/menu?category=donuts"
                    className="bg-[#FFD000] hover:bg-[#FFD933] text-black font-heading font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer"
                  >
                    <span>Fresh Hot Donuts (20 Flavours)</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <Link
                    to="/menu?category=drinks"
                    className="bg-[#f0f4ec] hover:bg-[#e4ebd8] text-[#000000] font-heading font-bold text-xs uppercase tracking-wider px-4 py-3 rounded-xl border border-[#dde0d5] transition-all cursor-pointer"
                  >
                    <span>Pint Slushies &amp; Ice Creams</span>
                  </Link>
                </div>
              </div>

              {/* Photo of Children enjoying the rides */}
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border-2 border-white aspect-[4/3] bg-sky-100">
                  <ClientImage
                    slotKey="site:childrenKiddiesCorner"
                    src="/childern_2.webp"
                    fallbackSrc={CLIENT_IMAGES.childrenKiddiesCorner}
                    alt="Children enjoying rides at Ramsgate Beach beside Frankie's"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="font-heading font-bold text-sm">Ramsgate Main Sands Seaside Fun</p>
                    <p className="text-[11px] text-white/80">Kiosk 1, Marina Esplanade, Kent CT11 8LS</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* 5. Frequently Asked Questions about Rides */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#0070E0] font-heading mb-1">
            <HelpCircle className="w-4 h-4 text-[#D1A03F]" />
            <span>COMMONLY ASKED QUESTIONS</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-black font-heading uppercase text-[#000000]">
            BEACH RIDES FAQ
          </h2>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-[#dde0d5] shadow-xs">
            <h4 className="font-heading font-bold text-sm sm:text-base text-[#000000] uppercase flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#0070E0]" />
              <span>When are the beach rides open?</span>
            </h4>
            <p className="text-xs sm:text-sm text-[#526b74] mt-1.5 leading-relaxed">
              The fair rides operate daily throughout the spring and summer season, on sunny weekends, bank holidays, and every day of the school holidays (weather permitting).
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#dde0d5] shadow-xs">
            <h4 className="font-heading font-bold text-sm sm:text-base text-[#000000] uppercase flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#0070E0]" />
              <span>Where exactly are the rides located?</span>
            </h4>
            <p className="text-xs sm:text-sm text-[#526b74] mt-1.5 leading-relaxed">
              Directly on the main sands at Marina Esplanade, Ramsgate Beach (CT11 8LS), right in front of Frankie’s @ The Beach kiosk and seating area.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-[#dde0d5] shadow-xs">
            <h4 className="font-heading font-bold text-sm sm:text-base text-[#000000] uppercase flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#0070E0]" />
              <span>Are parents allowed to accompany small children?</span>
            </h4>
            <p className="text-xs sm:text-sm text-[#526b74] mt-1.5 leading-relaxed">
              Yes! For toddler-friendly attractions such as the carousel, parents are welcome to stand right beside their child on the platform for guidance and reassurance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
